import { roundCents } from '@/utils/money'
import type { CalculatorContext } from '@/types/calculator'
import type { SolarRoiInputs, SolarRoiResult } from './types'

const GENERATION_PER_KW_PER_YEAR = 1650
/** Number of years over which savings are projected and ROI is measured. */
export const ANALYSIS_YEARS = 25

export function calculateSolarRoi(
  inputs: SolarRoiInputs,
  _context: CalculatorContext
): SolarRoiResult {
  const totalSystemCost = inputs.systemSizeKw * 1000 * inputs.systemCostPerW
  const estimatedAnnualGenerationKwh = inputs.systemSizeKw * GENERATION_PER_KW_PER_YEAR

  const usableGeneration = estimatedAnnualGenerationKwh * inputs.daytimeUsageShare
  const estimatedAnnualSavingsYear1 = usableGeneration * inputs.tariffPerKwh

  // Escalate savings over ANALYSIS_YEARS years
  let totalSavings = 0
  for (let year = 0; year < ANALYSIS_YEARS; year++) {
    totalSavings += estimatedAnnualSavingsYear1 * Math.pow(1 + inputs.annualTariffEscalation, year)
  }

  const netSavingsOver25Years = totalSavings - totalSystemCost
  const simpleRoi = totalSystemCost > 0 ? netSavingsOver25Years / totalSystemCost : 0
  const paybackYears =
    estimatedAnnualSavingsYear1 > 0 ? totalSystemCost / estimatedAnnualSavingsYear1 : 0

  let financedMonthlyPayment = 0
  let financedTotalCost = 0
  if (inputs.financingYears > 0 && inputs.financingRate > 0 && totalSystemCost > 0) {
    const n = inputs.financingYears * 12
    const r = inputs.financingRate / 12
    const factor = Math.pow(1 + r, n)
    financedMonthlyPayment = (totalSystemCost * r * factor) / (factor - 1)
    financedTotalCost = financedMonthlyPayment * n
  } else if (inputs.financingYears > 0 && totalSystemCost > 0) {
    financedMonthlyPayment = totalSystemCost / (inputs.financingYears * 12)
    financedTotalCost = totalSystemCost
  }

  const warning =
    'Solar savings depend heavily on location, roof orientation, shading, equipment, tariff structure and future tariff changes. Treat this as a wide estimate only.'

  return {
    systemSizeKw: roundCents(inputs.systemSizeKw),
    estimatedAnnualGenerationKwh: roundCents(estimatedAnnualGenerationKwh),
    estimatedAnnualSavings: roundCents(totalSavings / ANALYSIS_YEARS),
    estimatedAnnualSavingsYear1: roundCents(estimatedAnnualSavingsYear1),
    paybackYears: roundCents(paybackYears),
    simpleRoi,
    totalSystemCost: roundCents(totalSystemCost),
    financedMonthlyPayment: roundCents(financedMonthlyPayment),
    financedTotalCost: roundCents(financedTotalCost),
    netSavingsOver25Years: roundCents(netSavingsOver25Years),
    warning,
  }
}
